import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Receipt, CreditCard, Plus } from 'lucide-react';
import { format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

const statusColors = {
  draft: 'secondary',
  entered: 'default', 
  partially_paid: 'outline',
  paid: 'default',
  void: 'destructive'
} as const;

const statusLabels = {
  draft: 'Draft',
  entered: 'Entered',
  partially_paid: 'Partially Paid', 
  paid: 'Paid',
  void: 'Void'
} as const;

// Helper function to format currency
const formatCurrency = (amount: number, currency: string) => {
  return new Intl.NumberFormat('el-GR', {
    style: 'currency',
    currency: currency
  }).format(amount);
};

export const BillDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');

  // Load bill details
  const { data: bill, isLoading } = useQuery({
    queryKey: ['bill-detail', id],
    queryFn: async () => {
      if (!id) throw new Error('No bill ID provided');
      
      const { data, error } = await supabase
        .from('bills')
        .select(`
          *,
          suppliers:supplier_id (
            id,
            display_name,
            email,
            payment_terms
          ),
          bill_items (
            id,
            quantity,
            unit_price,
            products:product_id (
              name,
              category
            )
          )
        `)
        .eq('id', id)
        .single();

      if (error) throw error;
      return data;
    }
  });

  // Load payments for this bill
  const { data: payments } = useQuery({
    queryKey: ['bill-payments', id],
    queryFn: async () => {
      if (!id) return [];
      
      const { data, error } = await supabase
        .from('payment_allocations')
        .select(`
          *,
          payments:payment_id (
            id,
            amount,
            date,
            reference,
            direction
          )
        `)
        .eq('doc_id', id)
        .eq('doc_type', 'BILL');

      if (error) throw error;
      return data;
    }
  });

  // Calculate total paid
  const totalPaid = payments?.reduce((sum, alloc) => sum + alloc.amount_applied, 0) || 0;
  const remainingAmount = bill ? (bill.grand_total || bill.amount) - totalPaid : 0;

  // Record payment mutation
  const recordPayment = useMutation({
    mutationFn: async ({ amount }: { amount: number }) => {
      if (!bill) throw new Error('No bill loaded');

      // Create payment record
      const { data: payment, error: paymentError } = await supabase
        .from('payments')
        .insert([{
          amount,
          direction: 'OUT',
          date: format(new Date(), 'yyyy-MM-dd'),
          reference: `Payment for Bill ${bill.bill_number || bill.id.slice(-8)}`,
          method: 'BANK_TRANSFER'
        }])
        .select()
        .single();

      if (paymentError) throw paymentError;

      // Create payment allocation
      const { error: allocError } = await supabase
        .from('payment_allocations')
        .insert([{
          payment_id: payment.id,
          doc_id: bill.id,
          doc_type: 'BILL',
          amount_applied: amount
        }]);

      if (allocError) throw allocError;

      // Update bill status based on payment
      const newTotalPaid = totalPaid + amount;
      const billTotal = bill.grand_total || bill.amount;
      
      let newStatus = bill.status;
      if (newTotalPaid >= billTotal) {
        newStatus = 'paid';
      } else if (newTotalPaid > 0) {
        newStatus = 'partially_paid';
      }

      const { error: updateError } = await supabase
        .from('bills')
        .update({ 
          status: newStatus,
          paid_date: newTotalPaid >= billTotal ? format(new Date(), 'yyyy-MM-dd') : null
        })
        .eq('id', bill.id);

      if (updateError) throw updateError;

      // Create payment GL posting
      const { error: glError } = await supabase.rpc('create_payment_gl_posting', {
        payment_id_param: payment.id
      });

      if (glError) throw glError;

      return payment;
    },
    onSuccess: () => {
      toast({ title: "Payment recorded successfully" });
      queryClient.invalidateQueries({ queryKey: ['bill-detail', id] });
      queryClient.invalidateQueries({ queryKey: ['bill-payments', id] });
      setPaymentDialogOpen(false);
      setPaymentAmount('');
    },
    onError: (error: any) => {
      toast({
        title: "Error recording payment",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  if (isLoading) {
    return <div className="text-center py-8">Loading bill details...</div>;
  }

  if (!bill) {
    return <div className="text-center py-8">Bill not found</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/xero-lite/bills')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Bills
        </Button>
        <div>
          <h1 className="text-2xl font-bold">
            {bill.bill_number || `Bill #${bill.id.slice(-8)}`}
          </h1>
          <p className="text-muted-foreground">
            {bill.suppliers?.display_name} • {bill.bill_number}
          </p>
        </div>
      </div>

      {/* Bill Overview */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5" />
              Bill Overview
            </CardTitle>
            <Badge variant={statusColors[bill.status as keyof typeof statusColors]}>
              {statusLabels[bill.status as keyof typeof statusLabels]}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Amount</p>
              <p className="font-semibold">
                {formatCurrency(bill.grand_total || bill.amount, bill.currency)}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Paid</p>
              <p className="font-semibold text-green-600">
                {formatCurrency(totalPaid, bill.currency)}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Remaining</p>
              <p className={`font-semibold ${remainingAmount > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                {formatCurrency(remainingAmount, bill.currency)}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Due Date</p>
              <p className="font-semibold">
                {bill.due_date ? format(new Date(bill.due_date), "MMM dd, yyyy") : "—"}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="details" className="space-y-4">
        <TabsList>
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="payments">Payments</TabsTrigger>
        </TabsList>

        <TabsContent value="details">
          <Card>
            <CardHeader>
              <CardTitle>Bill Items</CardTitle>
            </CardHeader>
            <CardContent>
              {bill.bill_items && bill.bill_items.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Product</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead className="text-right">Quantity</TableHead>
                      <TableHead className="text-right">Unit Price</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bill.bill_items.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell className="font-medium">
                          {item.products?.name || 'Unknown Product'}
                        </TableCell>
                        <TableCell>
                          {item.products?.category || '—'}
                        </TableCell>
                        <TableCell className="text-right">{item.quantity}</TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(item.unit_price, bill.currency)}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(item.quantity * item.unit_price, bill.currency)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No line items found for this bill.
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="payments">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  Payments
                </CardTitle>
                {remainingAmount > 0 && bill.status !== 'void' && (
                  <Button onClick={() => setPaymentDialogOpen(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Record Payment
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {payments && payments.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Reference</TableHead>
                      <TableHead>Method</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payments.map((allocation) => (
                      <TableRow key={allocation.id}>
                        <TableCell>
                          {allocation.payments?.date ? 
                            format(new Date(allocation.payments.date), "MMM dd, yyyy") : 
                            "—"
                          }
                        </TableCell>
                        <TableCell>{allocation.payments?.reference || "—"}</TableCell>
                        <TableCell>
                          <Badge variant="outline">Bank Transfer</Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(allocation.amount_applied, bill.currency)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No payments recorded for this bill.
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Record Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="amount">Payment Amount</Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                placeholder="0.00"
                max={remainingAmount}
              />
              <p className="text-sm text-muted-foreground">
                Remaining: {formatCurrency(remainingAmount, bill.currency)}
              </p>
            </div>
            
            <div className="flex gap-2 pt-4">
              <Button
                variant="outline"
                onClick={() => setPaymentDialogOpen(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                onClick={() => recordPayment.mutate({ amount: parseFloat(paymentAmount) })}
                disabled={!paymentAmount || parseFloat(paymentAmount) <= 0 || recordPayment.isPending}
                className="flex-1"
              >
                {recordPayment.isPending ? 'Recording...' : 'Record Payment'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};